/**
 * Which Intl locale formats a given currency the way its users expect.
 *
 * The formatters used to hardcode `en-GB` for every currency, so an INR tenant
 * saw `₹370,057,815` — Western thousands grouping — where Indian convention is
 * lakh/crore grouping, `₹37,00,57,815`. The symbol and decimals come from the
 * currency code regardless of locale; only the digit grouping is locale-driven,
 * which is why this maps currency → locale rather than exposing a locale setting.
 *
 * Anything not listed keeps the caller's fallback, so GBP/USD/EUR output is
 * byte-for-byte unchanged.
 *
 * @param currency - ISO 4217 code, e.g. 'INR'
 * @param fallback - locale to use for every other currency (the caller's existing default)
 * @returns a BCP 47 locale tag for Intl.NumberFormat
 */
export function localeForCurrency(currency: string | undefined, fallback: string = 'en-GB'): string {
  switch ((currency || '').toUpperCase()) {
    case 'INR':
      return 'en-IN';
    default:
      return fallback;
  }
}

/**
 * Whether a currency groups digits the Indian way (3, then 2, 2, …).
 * Used by plain-number formatters that have no currency symbol to show but
 * still need to match the currency figures beside them.
 */
export function usesIndianGrouping(currency: string | undefined): boolean {
  return (currency || '').toUpperCase() === 'INR';
}

/**
 * Group an integer string the Indian way: last three digits, then pairs.
 * `37005781` → `3,70,05,781`. Separator is injectable so the tenant's configured
 * thousands separator is respected, as the Western path already does.
 */
export function groupIndian(integerDigits: string, separator: string = ','): string {
  if (integerDigits.length <= 3) return integerDigits;
  const last3 = integerDigits.slice(-3);
  const rest = integerDigits.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, separator);
  return `${rest}${separator}${last3}`;
}
